/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped resource: responses are passed through as the server sends them. */
import { SdkContext } from '../internal'
import type { CommentsToggleResult } from '@/lib/postComments'

/** What a reaction route answers: the target's reactions after the change. */
interface ReactionsResult {
  reactions: { type: string; count: number; reacted: boolean }[]
}

export class StatusResource {
  constructor(private ctx: SdkContext) {}

  async list(params: Record<string, any> = {}) {
    return this.ctx.call({ httpMethod: 'GET', path: '/status' }, { query: params })
  }

  async get(id: string): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: `/status/{id}` }, { id })
  }

  async create(statusData: any): Promise<any> {
    return this.ctx.call({ httpMethod: 'POST', path: '/status', hasBody: true }, { body: statusData })
  }

  async update(id: string, statusData: any): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/status/{id}`, hasBody: true },
      { id, body: statusData }
    )
  }

  /** F-163: turns the post's comments off or on. Sent alone, it changes nothing else (a moderator may do it on any post). */
  async setCommentsDisabled(id: string, commentsDisabled: boolean): Promise<CommentsToggleResult> {
    return this.ctx.call<CommentsToggleResult>(
      { httpMethod: 'PUT', path: `/status/{id}`, hasBody: true },
      { id, body: { commentsDisabled } }
    )
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/status/{id}` }, { id })
  }

  /** Adds the reaction, or swaps the one already there. */
  async react(id: string, reactionType: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/status/{id}/react`, hasBody: true },
      { id, body: { type: reactionType } }
    )
  }

  /** Takes the session's reaction off. */
  async unreact(id: string): Promise<ReactionsResult> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/status/{id}/react` }, { id })
  }

  /** `videoMediaId` (F-107): a Drop confirmed with the purpose `statusCommentVideo`; the text may then be empty. */
  async comment(id: string, content: string, parentId?: string, videoMediaId?: string | null): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/status/{id}/comment`, hasBody: true },
      { id, body: { content, ...(parentId ? { parentId } : {}), ...(videoMediaId ? { videoMediaId } : {}) } }
    )
  }

  async getComments(id: string, params: { page?: number; limit?: number } = {}): Promise<any> {
    return this.ctx.call({ httpMethod: 'GET', path: `/status/{id}/comments` }, { id, query: params })
  }

  async reactToComment(statusId: string, commentId: string, reactionType: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'POST', path: `/status/{statusId}/comment/{commentId}/react`, hasBody: true },
      { statusId, commentId, body: { type: reactionType } }
    )
  }

  async unreactToComment(statusId: string, commentId: string): Promise<ReactionsResult> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/status/{statusId}/comment/{commentId}/react` }, { statusId, commentId })
  }

  /** `videoMediaId`: another id swaps the Drop, `null` removes it, `undefined` keeps it. */
  async editComment(statusId: string, commentId: string, content: string, videoMediaId?: string | null): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: `/status/{statusId}/comment/{commentId}`, hasBody: true },
      { statusId, commentId, body: { content, ...(videoMediaId !== undefined ? { videoMediaId } : {}) } }
    )
  }

  async deleteComment(statusId: string, commentId: string): Promise<any> {
    return this.ctx.call(
      { httpMethod: 'DELETE', path: `/status/{statusId}/comment/{commentId}` },
      { statusId, commentId }
    )
  }
}
